<template>
  <div :class="isLoggedIn ? '' : 'public-checkout-page'">
    <template v-if="canRenderCheckout && isLoggedIn">
      <b-row class="flex-column">
        <b-col>
          <h2 class="font-weight-bold text-dark">
            Checkout
          </h2>
          <span class="font-weight-bolder mr-2">
            {{ $t('updatedPrices') }} {{ new Date().getDate() }}/{{
              new Date().getMonth() + 1
            }}/{{ new Date().getFullYear() }}
          </span>
          <b-badge
            class="mr-1"
            variant="info"
          >
            {{ $t('validOnly72hr') }}
          </b-badge>
        </b-col>
      </b-row>
      <b-row class="mt-2">
        <b-col>
          <b-badge
            class="mr-1"
            :variant="$helpers.quoteStatusColor(quote.quote_status)"
          >
            {{ $helpers.quoteStatusName(quote.quote_status) }}
          </b-badge>
          <b-badge :variant="$helpers.paymentStatusColor(quote.payment_status)">
            {{ $helpers.paymentStatusName(quote.payment_status) }}
          </b-badge>
        </b-col>
      </b-row>
      <b-row class="mt-2">
        <b-col>
          <checkout
            :key="checkoutViewKey"
            :quote="quote"
            :project="projectData"
            :items="quote.quote_items_attributes"
            :address="quote.address"
            :tax-info="projectData.tax_information || {}"
            :refetch="fetchData"
          />
        </b-col>
      </b-row>
    </template>

    <div
      v-else-if="canRenderCheckout"
      class="public-checkout-shell"
    >
      <section class="public-checkout-hero">
        <div class="public-checkout-hero__content">
          <span class="public-checkout-hero__eyebrow">
            Cotización pública
          </span>
          <h1 class="public-checkout-hero__title">
            {{ heroTitle }}
          </h1>
          <p class="public-checkout-hero__subtitle">
            {{ heroSubtitle }}
          </p>

          <div class="public-checkout-hero__chips">
            <b-badge
              pill
              :variant="$helpers.quoteStatusColor(quote.quote_status)"
            >
              {{ $helpers.quoteStatusName(quote.quote_status) }}
            </b-badge>
            <b-badge
              pill
              :variant="$helpers.paymentStatusColor(quote.payment_status)"
            >
              {{ $helpers.paymentStatusName(quote.payment_status) }}
            </b-badge>
            <b-badge
              pill
              variant="info"
            >
              {{ $t('validOnly72hr') }}
            </b-badge>
          </div>
        </div>

        <aside class="public-checkout-hero__meta">
          <div class="public-checkout-meta-card">
            <div class="public-checkout-meta-card__row">
              <span class="public-checkout-meta-card__label">Proyecto</span>
              <strong>{{ projectData.folio ? `#${projectData.folio}` : '—' }}</strong>
            </div>
            <div class="public-checkout-meta-card__row">
              <span class="public-checkout-meta-card__label">Cotización</span>
              <strong>{{ quote.folio ? `#${quote.folio}` : '—' }}</strong>
            </div>
            <div class="public-checkout-meta-card__row">
              <span class="public-checkout-meta-card__label">Emitida</span>
              <strong>{{ issuedAtText }}</strong>
            </div>
            <div class="public-checkout-meta-card__row">
              <span class="public-checkout-meta-card__label">Actualizada</span>
              <strong>{{ todayText }}</strong>
            </div>
          </div>
        </aside>
      </section>

      <section class="public-checkout-body">
        <checkout
          :key="checkoutViewKey"
          :quote="quote"
          :project="projectData"
          :items="quote.quote_items_attributes"
          :address="quote.address"
          :tax-info="projectData.tax_information || {}"
          :refetch="fetchData"
        />
      </section>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Checkout from '@/components/invoice/Checkout.vue'
import { getHomeRouteForLoggedInUser } from '@/auth/utils'

export default {
  components: {
    Checkout,
  },

  data() {
    return {
      projectData: {},
      canRenderCheckout: false,
      isFetchingQuote: false,
      accessErrorHandled: false,
      items: [
        {
          folio: '91716',
          address: 'Depotivo Cumbres',
          municipality: 'Monterrey, N.L.',
          postal_code: '67110',
          date: '06 Ago 2021',
        },
      ],
    }
  },

  computed: {
    ...mapGetters('products', ['products']),
    ...mapGetters('quotes', ['quote']),
    ...mapGetters('auth', ['currentUser']),
    isLoggedIn() {
      return Boolean(this.currentUser?.role_name)
    },
    checkoutViewKey() {
      return `${this.$route.params.quoteId || 'quote'}:${this.currentUser?.role_name || 'guest'}`
    },
    heroTitle() {
      return this.projectData?.name || 'Cotización YoConstruyo'
    },
    heroSubtitle() {
      return 'Comparte esta cotización de forma segura con tu cliente y permite que la revise antes de iniciar sesión para pagar.'
    },
    todayText() {
      const today = new Date()
      return today.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    },
    issuedAtText() {
      if (!this.quote?.published_at) return '—'
      const issuedAt = new Date(this.quote.published_at)
      if (Number.isNaN(issuedAt.getTime())) return '—'
      return issuedAt.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    },
  },

  created() {
    this.fetchData()
  },

  watch: {
    async isLoggedIn(newValue, oldValue) {
      if (newValue && !oldValue) {
        this.canRenderCheckout = false
        await this.fetchData()
      }
    },
  },

  methods: {
    ...mapActions('quotes', ['fetchQuote', 'getQuotePayments']),
    toast(title, variant = 'warning') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: 'AlertTriangleIcon',
          variant,
        },
      })
    },

    // La apertura la manda la cotización, no el cliente: el admin puede abrir o cerrar
    // una sola. Si el backend no la manda, se cae al ajuste del cliente.
    isQuoteOpenForCustomer(quote) {
      if (quote && quote.open_quotation_enabled !== undefined && quote.open_quotation_enabled !== null) {
        return Boolean(quote.open_quotation_enabled)
      }

      return Boolean(this.currentUser?.customer?.openQuotation)
    },

    // Etiquetas estables por sucursal, con el mismo esquema que usa el backend para el
    // cliente: el mismo proveedor lleva el mismo numero en toda la cotizacion.
    buildSupplierMasker() {
      const labels = {}

      return supplierBranchId => {
        const key = String(supplierBranchId || '')
        if (!key) return 'No hay proveedor'

        if (!labels[key]) labels[key] = `Proveedor ${Object.keys(labels).length + 1}`

        return labels[key]
      }
    },

    mapQuoteItemsWithSupplier(items = [], quote = null) {
      const quoteIsOpen = this.isQuoteOpenForCustomer(quote)
      const isSupplierViewer = ['auctioneer', 'supplier_admin'].includes(this.currentUser?.role_name)
      const maskedLabelFor = this.buildSupplierMasker()

      return items.map(item => {
        if (item.selected_supplier_branch_id === null) {
          return {
            ...item,
            supplier: 'No hay proveedor',
          }
        }

        // El proveedor siempre se ve a si mismo. Fuera de eso, el admin ve la cotizacion
        // igual que el cliente: si esta cerrada, tampoco le salen los nombres.
        if (quoteIsOpen || isSupplierViewer) {
          return {
            ...item,
            supplier: item.selected_supplier_branch_name,
          }
        }

        // Se pisa selected_supplier_branch_name y no solo supplier porque la tabla resuelve
        // la etiqueta desde ahi primero, y al admin el backend le manda el nombre real.
        const masked = maskedLabelFor(item.selected_supplier_branch_id)

        return {
          ...item,
          supplier: masked,
          selected_supplier_branch_name: masked,
          // La mascara es para que el admin vea la cotizacion como el cliente; sus propias
          // herramientas (Imprimir OC) siguen necesitando el nombre real.
          unmasked_supplier_branch_name: item.selected_supplier_branch_name,
          selected_supplier_branch_phone: null,
          unit_prices: Array.isArray(item.unit_prices)
            ? item.unit_prices.map(offer => ({
              ...offer,
              supplier_branch_name: maskedLabelFor(offer.supplier_branch_id),
            }))
            : item.unit_prices,
        }
      })
    },

    async fetchData() {
      if (this.isFetchingQuote) return null

      this.isFetchingQuote = true

      try {
        const response = await this.fetchQuote(this.$route.params.quoteId)
        response.quote_items_attributes = this.mapQuoteItemsWithSupplier(response.quote_items_attributes, response)
        this.projectData = response.project_attributes || {}
        this.accessErrorHandled = false

        if (this.isLoggedIn) {
          await this.getQuotePayments({ quoteId: this.$route.params.quoteId })
        }

        this.canRenderCheckout = true

        return response
      } catch (error) {
        this.canRenderCheckout = false

        if (this.accessErrorHandled) return null

        this.accessErrorHandled = true
        const status = error?.status
        const messages = error?.data?.messages || []
        const title = messages[0] || 'No fue posible cargar la cotización.'

        this.toast(title, 'danger')

        if (status === 403 && this.isLoggedIn) {
          this.$router.replace(getHomeRouteForLoggedInUser(this.currentUser))
          return null
        }

        if (status === 403 || status === 404) {
          this.$router.replace({ name: this.isLoggedIn ? getHomeRouteForLoggedInUser(this.currentUser).name : 'auth-login' })
          return null
        }

        throw error
      } finally {
        this.isFetchingQuote = false
      }
    },
  },
}
</script>

<style scoped>
.public-checkout-page {
  min-height: 100vh;
  padding: 2.25rem 1.25rem 3rem;
  background:
    radial-gradient(circle at top left, rgba(52, 6, 104, 0.12), transparent 32%),
    linear-gradient(180deg, #f8f6ff 0%, #f7f7fb 42%, #ffffff 100%);
}

.public-checkout-shell {
  width: 100%;
  max-width: 1640px;
  margin: 0 auto;
}

.public-checkout-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(280px, 360px);
  gap: 1.5rem;
  align-items: stretch;
  margin-bottom: 1.5rem;
}

.public-checkout-hero__content {
  position: relative;
  overflow: hidden;
  padding: 2rem 2.25rem;
  border: 1px solid rgba(92, 75, 135, 0.12);
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(52, 6, 104, 0.98) 0%, rgba(78, 19, 148, 0.94) 100%);
  box-shadow: 0 24px 60px rgba(38, 17, 78, 0.18);
  color: #fff;
}

.public-checkout-hero__content::after {
  content: '';
  position: absolute;
  inset: auto -10% -35% auto;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
}

.public-checkout-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.84);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.public-checkout-hero__title {
  margin: 1rem 0 0.75rem;
  color: #fff;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  line-height: 1.05;
}

.public-checkout-hero__subtitle {
  max-width: 62ch;
  margin: 0;
  color: rgba(255, 255, 255, 0.82);
  font-size: 1rem;
  line-height: 1.7;
}

.public-checkout-hero__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 1.4rem;
}

.public-checkout-hero__chips ::v-deep .badge {
  padding: 0.5rem 0.9rem;
  font-size: 0.82rem;
  font-weight: 700;
}

.public-checkout-hero__meta {
  display: flex;
}

.public-checkout-meta-card {
  width: 100%;
  padding: 1.35rem 1.4rem;
  border: 1px solid rgba(92, 75, 135, 0.12);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 18px 45px rgba(53, 43, 89, 0.08);
  backdrop-filter: blur(14px);
}

.public-checkout-meta-card__row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  padding: 0.9rem 0;
  border-bottom: 1px solid rgba(92, 75, 135, 0.1);
  color: #433b5b;
}

.public-checkout-meta-card__row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.public-checkout-meta-card__row:first-child {
  padding-top: 0;
}

.public-checkout-meta-card__label {
  color: #7a7391;
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.public-checkout-body {
  margin-top: 0;
}

@media (max-width: 991.98px) {
  .public-checkout-page {
    padding: 1rem 0.75rem 2rem;
  }

  .public-checkout-hero {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .public-checkout-hero__content {
    padding: 1.5rem;
    border-radius: 22px;
  }

  .public-checkout-hero__title {
    font-size: 2rem;
  }
}

@media print {
  .public-checkout-page {
    padding: 0 !important;
    background: #fff !important;
  }

  .public-checkout-shell {
    max-width: 100% !important;
    margin: 0 !important;
  }

  .public-checkout-hero {
    display: none !important;
  }

  .public-checkout-body {
    margin-top: 0 !important;
  }
}
</style>
